import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { CollectionPopupComponent } from '../../components/shared/collection-popup/collection-popup.component';
import { ApiService } from 'src/app/services/api.service';
import { DownloadService, DownloadTarget } from 'src/app/services/download.service';
import { PopupService } from 'src/app/services/popup.service';

@Injectable({
  providedIn: 'root',
})
export class IndicesDownloadService {
  private freeze = false;

  constructor(
    private apiService: ApiService,
    private popupService: PopupService,
    private spinnerService: NgxSpinnerService,
    private downloadService: DownloadService
  ) {}

  async downloadIndices(
    indices: any,
    collectionIndex: number,
    sensor: any,
    sensorIndex: number,
    taskId: string
  ) {
    const currentScene = sensor.scenes.find((scene) => scene.checked);

    const isDownloaded = indices.products?.some(
      (el) => el.sceneId === currentScene.id && el.isDownloaded
    );

    if (this.freeze) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        'A download is in progress. Please wait until the current download finishes to continue downloading others.',
        '',
        '40%'
      );
      return;
    }

    if (isDownloaded) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        'download_vadp',
        '',
        '40%'
      );
      return;
    }

    this.spinnerService.show();
    this.freeze = true;

    const sensorObj = {
      taskId: taskId,
      sensorId: indices.sensorId,
      sceneId: currentScene.id,
      product: indices.product,
      type: 'download',
    };

    this.apiService.getIndicesDataImgUrl(sensorObj).subscribe((res: any) => {
      const urls = res?.response?.signedUrls || [];
      if (!urls.length) {
        this.spinnerService.hide();
        this.freeze = false;
        return;
      }

      this.downloadUrlsSequentially(
        urls,
        taskId,
        sensor,
        currentScene,
        indices.product
      )
        .then(() => {
          this.spinnerService.hide();
          this.freeze = false;
        })
        .catch((error: any) => {
          this.spinnerService.hide();
          this.freeze = false;
          if (error?.message === 'Download cancelled.') {
            return;
          }
          this.popupService.openPopup(
            CollectionPopupComponent,
            'Failed to download one or more files.',
            '',
            '40%'
          );
        });
    });
  }

  private async downloadUrlsSequentially(
    urls: any[],
    taskId: string,
    sensor: any,
    currentScene: any,
    product: string
  ) {
    for (const item of urls) {
      const url = item.imageUrl;
      if (!url) continue;

      await this.downloadFile(url, taskId, sensor, currentScene, product);
    }
  }

  private downloadFile(
    url: string,
    taskId: string,
    sensor: any,
    currentScene: any,
    product: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const fileName = this.getFileNameFromS3SignedUrl(url);
      this.resolveDownloadTarget(fileName).then((downloadTarget) => {
        if (!downloadTarget) {
          reject(new Error('Download cancelled.'));
          return;
        }

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.responseType = 'blob';

      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = ((event.loaded / event.total) * 100).toFixed(
            2
          );
          this.downloadService.setProgress({
            progress: percentComplete,
            taskId,
            sensorId: sensor.id,
            sceneId: currentScene.id,
            download: true,
            product,
            action: 'indicesDownload',
          });
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          this.downloadService.saveBlob(blob, fileName, downloadTarget).then((saveResult) => {
            if (saveResult.status === 'saved') {
              resolve();
              return;
            }

            if (saveResult.status === 'cancelled') {
              reject(new Error('Download cancelled.'));
              return;
            }

            reject(new Error(saveResult.message));
          });
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send();
      });
    });
  }

  private async resolveDownloadTarget(
    suggestedFileName: string
  ): Promise<DownloadTarget | null> {
    if (!this.downloadService.isFolderPickerSupported()) {
      return { mode: 'browser' };
    }

    const selection = await this.downloadService.pickSaveFileForDownload(
      suggestedFileName
    );
    if (selection.status === 'selected') {
      return { mode: 'saveFile', fileHandle: selection.handle };
    }

    if (selection.status === 'unsupported') {
      return { mode: 'browser' };
    }

    if (selection.status !== 'cancelled') {
      this.popupService.openPopup(
        CollectionPopupComponent,
        selection.message,
        '',
        '40%'
      );
    }

    return null;
  }

  private getFileNameFromS3SignedUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      const rawContentDisposition =
        parsedUrl.searchParams.get('response-content-disposition') ||
        parsedUrl.searchParams.get('content-disposition');

      if (rawContentDisposition) {
        const decodedContentDisposition = decodeURIComponent(
          rawContentDisposition
        );
        const utfFileNameMatch = decodedContentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i
        );
        if (utfFileNameMatch?.[1]) {
          return utfFileNameMatch[1].replace(/"/g, '').trim();
        }

        const fileNameMatch = decodedContentDisposition.match(
          /filename="?([^";]+)"?/i
        );
        if (fileNameMatch?.[1]) {
          return fileNameMatch[1].trim();
        }
      }

      const decodedPathName = decodeURIComponent(parsedUrl.pathname || '');
      const pathFileName = decodedPathName.split('/').pop();
      if (pathFileName) {
        return pathFileName;
      }
    } catch {
      // Fall through to legacy parsing.
    }

    const parts = url.split('?')[0].split('/');
    try {
      return decodeURIComponent(parts[parts.length - 1]);
    } catch {
      return parts[parts.length - 1];
    }
  }
}
